import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { standaloneFieldSx, standaloneFloatingSelectSx } from '../../common/fieldStyles';
import { formatCurrency } from '../../common/formatters';
import { httpClient } from '../../common/httpclient';
import DialogForm from '../../component/dialog/DialogForm';

// Lançamento manual de conta a receber: POST /transactions/receivables
// (`fv-api/transaction/receivable_creator.go`). Nasce sempre como receita Pendente,
// sem movimentar o caixa; ainda não há baixa de recebimento nesta fase.
//   body: { customer_id?, description, amount ("1.234,56"), due_date (YYYY-MM-DD) }
// Clientes: GET /customers (lista completa de ativos -> [{ ID, Name, ... }]), carregada
// só na primeira abertura do dialog para não pesar a listagem em tenants grandes.

const todayIso = () => dayjs().format('YYYY-MM-DD');

const parseAmountCents = (value) => Number(String(value || '').replace(/\D/g, '')) || 0;

export default function ReceivableCreateDialog({ open, onClose, onSuccess, onError }) {
    const [customers, setCustomers] = useState([]);
    const [customersLoaded, setCustomersLoaded] = useState(false);
    const [customerId, setCustomerId] = useState('');
    const [description, setDescription] = useState('');
    const [amount, setAmount] = useState('');
    const [dueDate, setDueDate] = useState(todayIso());
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);

    // Reinicia o formulário a cada abertura.
    useEffect(() => {
        if (!open) return;
        setCustomerId('');
        setDescription('');
        setAmount('');
        setDueDate(todayIso());
        setErrors({});
    }, [open]);

    useEffect(() => {
        if (!open || customersLoaded) return;
        httpClient.get('/customers')
            .then((response) => {
                setCustomers(Array.isArray(response.data) ? response.data : []);
                setCustomersLoaded(true);
            })
            .catch(() => setCustomers([]));
    }, [open, customersLoaded]);

    const validate = () => {
        const next = {};
        if (!description.trim()) next.description = 'Descrição é obrigatória';
        if (parseAmountCents(amount) <= 0) next.amount = 'Valor deve ser maior que zero';
        if (!/^\d{4}-\d{2}-\d{2}$/.test(dueDate) || !dayjs(dueDate).isValid()) next.dueDate = 'Data de vencimento é obrigatória';
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const handleSubmit = async () => {
        if (saving || !validate()) return;
        setSaving(true);
        try {
            await httpClient.post('/transactions/receivables', {
                customer_id: customerId || undefined,
                description: description.trim(),
                amount,
                due_date: dueDate,
            });
            onSuccess();
        } catch (error) {
            const data = error?.response?.data;
            const text = typeof data === 'string' ? data.trim() : (data?.error || data?.message || '');
            onError(text || 'Não foi possível lançar a conta a receber.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <DialogForm
            open={open}
            iconType="info"
            title="Nova conta a receber"
            subtitle="Lança uma receita pendente. Ela não entra no caixa nem no faturamento até ser recebida."
            confirmButtonText="Salvar"
            cancelButtonText="Cancelar"
            loading={saving}
            loadingText="Salvando…"
            onClose={() => { if (!saving) onClose(); }}
            onSubmit={() => { void handleSubmit(); }}
        >
            <TextField
                select
                fullWidth
                label="Cliente (opcional)"
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                SelectProps={{ displayEmpty: true, MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
                sx={standaloneFloatingSelectSx}
            >
                <MenuItem value="">Nenhum</MenuItem>
                {customers.map((c) => (
                    <MenuItem key={c.ID} value={c.ID}>{c.Name}</MenuItem>
                ))}
            </TextField>
            <TextField
                fullWidth
                label="Descrição *"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                inputProps={{ maxLength: 255 }}
                error={Boolean(errors.description)}
                helperText={errors.description}
                variant="outlined"
                sx={standaloneFieldSx}
            />
            <TextField
                fullWidth
                label="Valor (R$) *"
                value={amount}
                onChange={(e) => setAmount(formatCurrency(e.target.value))}
                inputProps={{ inputMode: 'numeric' }}
                error={Boolean(errors.amount)}
                helperText={errors.amount}
                variant="outlined"
                sx={standaloneFieldSx}
            />
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="pt-br">
                <DatePicker
                    label="Vencimento *"
                    format="DD/MM/YYYY"
                    value={dueDate ? dayjs(dueDate) : null}
                    onChange={(v) => setDueDate(v && v.isValid() ? v.format('YYYY-MM-DD') : '')}
                    slotProps={{
                        textField: {
                            fullWidth: true,
                            error: Boolean(errors.dueDate),
                            helperText: errors.dueDate,
                            sx: {
                                ...standaloneFieldSx,
                                '& .MuiPickersOutlinedInput-root': {
                                    height: '52px',
                                    borderRadius: '12px',
                                    backgroundColor: 'background.subtle',
                                },
                                '& .MuiPickersInputBase-sectionsContainer': {
                                    fontSize: '13.5px',
                                    padding: '21px 0 7px 0',
                                },
                            },
                        },
                    }}
                />
            </LocalizationProvider>
        </DialogForm>
    );
}
